import {GUEST_STATUSES, ShortGuest} from '@chekin/core';

export type GuestRowState = 'registered' | 'verify' | 'details' | 'empty';

export function getGuestRowState(guest?: {
  id?: string;
  guestapp_status?: GUEST_STATUSES;
}): GuestRowState {
  if (!guest?.id) return 'empty';
  if (guest.guestapp_status === GUEST_STATUSES.complete) return 'registered';
  if (guest.guestapp_status === GUEST_STATUSES.verification_pending) return 'verify';
  return 'details';
}

export const ROW_SUBTITLE_KEY: Partial<Record<GuestRowState, string>> = {
  verify: 'details_saved_verification_pending',
  details: 'waiting_for_details',
};

type RosterInput = {
  members?: ShortGuest[];
  leaderId?: string;
  missingGuests?: number;
};

function orderRoster(members: ShortGuest[] = [], leaderId?: string) {
  const leader = members.find(member => member.id === leaderId);
  const others = members.filter(member => member.id !== leaderId);

  return leader ? [leader, ...others] : others;
}

export type NextGuestAction =
  | {kind: 'registerSelf'}
  | {kind: 'verify'; guest: ShortGuest}
  | {kind: 'details'; guest: ShortGuest}
  | {kind: 'addGuest'; ordinal: number}
  | {kind: 'done'};

export function getNextGuestAction({
  members = [],
  leaderId,
  missingGuests = 0,
}: RosterInput): NextGuestAction {
  const roster = orderRoster(members, leaderId);
  const leader = members.find(member => member.id === leaderId);
  const leaderState = getGuestRowState(leader);

  if (leaderState === 'empty' || leaderState === 'details') {
    return {kind: 'registerSelf'};
  }

  const pendingVerification = roster.find(
    member => getGuestRowState(member) === 'verify',
  );
  if (pendingVerification) {
    return {kind: 'verify', guest: pendingVerification};
  }

  const missingDetails = roster.find(member => getGuestRowState(member) === 'details');
  if (missingDetails) {
    return {kind: 'details', guest: missingDetails};
  }

  if (missingGuests > 0) {
    return {kind: 'addGuest', ordinal: roster.length + 1};
  }

  return {kind: 'done'};
}

export function getNextActionGuestId(action: NextGuestAction, leaderId?: string) {
  if (action.kind === 'registerSelf') return leaderId;
  if (action.kind === 'verify' || action.kind === 'details') return action.guest.id;

  return undefined;
}

export type RosterEntry =
  | {kind: 'guest'; guest: ShortGuest; state: Exclude<GuestRowState, 'empty'>}
  | {kind: 'empty'; ordinal: number};

export type RosterSections = {
  holder: RosterEntry;
  others: RosterEntry[];
};

const ROSTER_RANK: Record<GuestRowState, number> = {
  verify: 0,
  details: 0,
  empty: 1,
  registered: 2,
};

function toEntry(guest: ShortGuest): RosterEntry {
  const state = getGuestRowState(guest);
  if (state === 'empty') return {kind: 'empty', ordinal: 0};
  return {kind: 'guest', guest, state};
}

function rankOf(entry: RosterEntry) {
  return entry.kind === 'empty' ? ROSTER_RANK.empty : ROSTER_RANK[entry.state];
}

export function buildRosterSections({
  members = [],
  leaderId,
  missingGuests = 0,
}: RosterInput): RosterSections {
  const leader = members.find(member => member.id === leaderId);
  const holder: RosterEntry = leader ? toEntry(leader) : {kind: 'empty', ordinal: 1};
  const holderSlots = leader ? 0 : 1;
  const emptyOthers = Math.max(missingGuests - holderSlots, 0);
  const addedCount = members.length + holderSlots;

  const others = [
    ...members.filter(member => member.id !== leaderId).map(toEntry),
    ...Array.from(
      {length: emptyOthers},
      (_, index): RosterEntry => ({kind: 'empty', ordinal: addedCount + index + 1}),
    ),
  ]
    .map((entry, index) => ({entry, index}))
    .sort((a, b) => rankOf(a.entry) - rankOf(b.entry) || a.index - b.index)
    .map(({entry}) => entry);

  return {holder, others};
}
